import React, { useCallback, useEffect, useState } from 'react';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw, Save, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  adminTrialMonitoringApi,
  FunnelStats,
  UsageToday,
  RateLimitConfig,
  FunnelFeature,
  FunnelEventType,
} from '@/api/adminTrialMonitoringApi';
import { Spinner } from '@/components/ui/spinner';

const FEATURES: FunnelFeature[] = ['TRIAL', 'DIAGNOSE'];
const EVENT_TYPES: FunnelEventType[] = ['START', 'RESULT_SHOWN', 'SIGNUP_CTA_CLICK'];
const FEATURE_LABELS: Record<FunnelFeature, string> = { TRIAL: '무료체험', DIAGNOSE: '무료진단' };
const EVENT_LABELS: Record<FunnelEventType, string> = {
  START: '시작',
  RESULT_SHOWN: '결과 확인',
  SIGNUP_CTA_CLICK: '가입 CTA 클릭',
};
const CONFIG_KEY_LABELS: Record<string, string> = {
  TRIAL_IP_DAILY_LIMIT: '무료체험 - IP별 1일 상한',
  TRIAL_GLOBAL_DAILY_LIMIT: '무료체험 - 전체 합산 1일 상한',
  DIAGNOSE_IP_DAILY_LIMIT: '무료진단 - IP별 1일 상한',
  DIAGNOSE_GLOBAL_DAILY_LIMIT: '무료진단 - 전체 합산 1일 상한',
};

export default function TrialMonitoringPage() {
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();

  const [days, setDays] = useState(7);
  const [stats, setStats] = useState<FunnelStats | null>(null);
  const [usageToday, setUsageToday] = useState<UsageToday | null>(null);
  const [rateLimits, setRateLimits] = useState<RateLimitConfig[]>([]);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, usageRes, limitsRes] = await Promise.all([
        adminTrialMonitoringApi.getStats(days),
        adminTrialMonitoringApi.getUsageToday(),
        adminTrialMonitoringApi.getRateLimits(),
      ]);
      setStats(statsRes);
      setUsageToday(usageRes);
      setRateLimits(limitsRes);
      setEditValues(Object.fromEntries(limitsRes.map((c) => [c.configKey, String(c.limitValue)])));
    } catch (err: any) {
      setError(err.response?.data?.message || '데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleSave = async (configKey: string) => {
    const value = parseInt(editValues[configKey], 10);
    if (isNaN(value) || value < 0) {
      setError('0 이상의 정수를 입력해주세요.');
      return;
    }
    setSavingKey(configKey);
    setError(null);
    setSaveMessage(null);
    try {
      const updated = await adminTrialMonitoringApi.updateRateLimit(configKey, value);
      setRateLimits((prev) => prev.map((c) => (c.configKey === configKey ? updated : c)));
      setSaveMessage(`${CONFIG_KEY_LABELS[configKey] || configKey} - ${value}로 즉시 반영됨`);
      // 사용량 상한도 함께 갱신됐을 수 있으니 재조회
      adminTrialMonitoringApi.getUsageToday().then(setUsageToday).catch(() => {});
    } catch (err: any) {
      setError(err.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title={menuName || '무료체험/무료진단 모니터링'}
        description="비회원 대상 무료체험·무료진단 전환 퍼널과 일일 상한을 관리합니다."
        icon={menuIcon}
        action={
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
            {loading ? <Spinner size="sm" className="mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            새로고침
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {saveMessage && (
        <Alert className="mb-4 border-green-200 bg-green-50 text-green-800">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{saveMessage}</AlertDescription>
        </Alert>
      )}

      {/* 오늘자 전역 사용량 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">오늘자 전역 사용량</CardTitle>
          <CardDescription>Redis 카운터 - 값이 -1이면 Redis 장애로 조회 불가</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {usageToday && (['trial', 'diagnose'] as const).map((key) => {
              const usage = usageToday[key];
              const isUnavailable = usage.globalCount < 0;
              const rate = !isUnavailable && usage.globalLimit > 0 ? Math.min(100, Math.round((usage.globalCount / usage.globalLimit) * 100)) : 0;
              return (
                <div key={key} className="border rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">{key === 'trial' ? '무료체험' : '무료진단'}</p>
                  {isUnavailable ? (
                    <p className="text-sm text-red-600">조회 불가 (Redis 장애)</p>
                  ) : (
                    <>
                      <p className="text-2xl font-bold">
                        {usage.globalCount.toLocaleString()} <span className="text-sm text-gray-400 font-normal">/ {usage.globalLimit.toLocaleString()}</span>
                      </p>
                      <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
                        <div
                          className={`h-2 rounded-full ${rate >= 90 ? 'bg-red-500' : rate >= 70 ? 'bg-orange-400' : 'bg-blue-500'}`}
                          style={{ width: `${rate}%` }}
                        />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* 퍼널 통계 */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base">전환 퍼널 통계</CardTitle>
              <CardDescription>시작 → 결과 확인 → 가입 CTA 클릭</CardDescription>
            </div>
            <div className="flex gap-1">
              {[1, 7, 30].map((d) => (
                <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'} onClick={() => setDays(d)}>
                  최근 {d}일
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="py-2 text-left font-medium text-gray-700">기능</th>
                  {EVENT_TYPES.map((et) => (
                    <th key={et} className="py-2 text-left font-medium text-gray-700">{EVENT_LABELS[et]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((feature) => (
                  <tr key={feature} className="border-b">
                    <td className="py-2 text-gray-800">{FEATURE_LABELS[feature]}</td>
                    {EVENT_TYPES.map((et) => (
                      <td key={et} className="py-2 text-gray-600">
                        {stats?.[feature]?.[et]?.toLocaleString() ?? '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 일일 상한 조정 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">일일 상한 조정</CardTitle>
          <CardDescription>저장하면 코드 배포/재시작 없이 즉시 반영됩니다.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {rateLimits.map((config) => (
              <div key={config.configKey} className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <p className="text-sm font-medium text-gray-800">{CONFIG_KEY_LABELS[config.configKey] || config.configKey}</p>
                  {config.updatedBy && (
                    <p className="text-xs text-gray-400">마지막 수정: {config.updatedBy}, {new Date(config.updatedAt).toLocaleString('ko-KR')}</p>
                  )}
                </div>
                <Input
                  type="number"
                  min={0}
                  value={editValues[config.configKey] ?? ''}
                  onChange={(e) => setEditValues((prev) => ({ ...prev, [config.configKey]: e.target.value }))}
                  className="w-28"
                />
                <Button
                  size="sm"
                  onClick={() => handleSave(config.configKey)}
                  disabled={savingKey === config.configKey || editValues[config.configKey] === String(config.limitValue)}
                >
                  {savingKey === config.configKey ? <Spinner size="sm" /> : <Save className="w-4 h-4" />}
                </Button>
              </div>
            ))}
            {rateLimits.length === 0 && !loading && (
              <p className="text-sm text-gray-400">설정값이 없습니다.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
